import { isValidInviteEmail } from "../components/invite_clients/parseInviteClientImport"

export type ClientImportReviewRowState = {
  rowKey: string
  firstName: string
  lastName: string
  email: string
  phone: string
  notes: string
  emailValid: boolean
  phoneValid: boolean
  phoneCheck: "clean" | "dirty"
}

const EMAIL_SPLIT_RE = /,|;|\/|\||\s+or\s+/gi

function stripEmailCellWrappers(raw: string): string {
  let s = raw.trim().replace(/^\uFEFF/, "")
  if (s.startsWith("'")) s = s.slice(1).trimStart()
  if (/^mailto:/i.test(s)) s = s.replace(/^mailto:/i, "").trim()
  const angle = s.match(/^<(.+)>$/)
  if (angle) s = angle[1]!.trim()
  const quoted = s.match(/^["'](.+)["']$/)
  if (quoted) s = quoted[1]!.trim()
  return s
}

function normalizeEmailToken(token: string): string {
  let t = token.trim()
  if (t.startsWith("'")) t = t.slice(1).trimStart()
  t = t.replace(/[.,;]+$/, "").trim()
  return t.toLowerCase()
}

function notesAlreadyContainsAddress(notes: string, address: string): boolean {
  return notes.toLowerCase().includes(address.toLowerCase())
}

function appendAlsoNotes(existingNotes: string, extras: string[]): string {
  const filtered = extras.filter((addr) => !notesAlreadyContainsAddress(existingNotes, addr))
  if (filtered.length === 0) return existingNotes
  const suffix = `Also: ${filtered.join(", ")}`
  if (!existingNotes.trim()) return suffix
  return `${existingNotes.trim()}\n${suffix}`
}

/** Deterministic email cleanup before server / LLM pass. */
export function applyEmailHeuristic(row: {
  email: string
  notes: string
}): { email: string; notes: string } {
  const prepared = stripEmailCellWrappers(row.email)
  if (!prepared) {
    return { email: "", notes: row.notes }
  }

  if (isValidInviteEmail(prepared)) {
    return { email: prepared.trim().toLowerCase(), notes: row.notes }
  }

  const tokens = prepared
    .split(EMAIL_SPLIT_RE)
    .map(normalizeEmailToken)
    .filter(Boolean)
  const valid = tokens.filter((t) => isValidInviteEmail(t))

  if (valid.length === 0) {
    return { email: row.email, notes: row.notes }
  }

  const primary = valid[0]!
  const extras = valid.slice(1)
  const notes = appendAlsoNotes(row.notes, extras)
  return { email: primary, notes }
}

export function computeEmailValid(email: string): boolean {
  const trimmed = email.trim()
  return trimmed === "" || isValidInviteEmail(trimmed)
}

export function rowBlocksImport(row: {
  email: string
  phone: string
  emailValid: boolean
  phoneValid: boolean
  phoneCheck: "clean" | "dirty"
}): boolean {
  if (row.phoneCheck === "dirty") return true

  const email = row.email.trim()
  const phone = row.phone.trim()
  const emailInvalid = email !== "" && !row.emailValid
  const phoneInvalid = phone !== "" && !row.phoneValid
  const bothEmpty = email === "" && phone === ""
  return emailInvalid || phoneInvalid || bothEmpty
}

export function blockingRowCount(rows: ClientImportReviewRowState[]): number {
  return rows.filter((r) => rowBlocksImport(r)).length
}

/** Review-form control. Available whenever the grid has rows, including rows that already validate. */
export function canTransformClientImportWithAi(input: {
  rowCount: number
  previewLoading: boolean
  fixing: boolean
  importing: boolean
}): boolean {
  return input.rowCount > 0 && !input.previewLoading && !input.fixing && !input.importing
}
